<template>
  <div class="campaign-v2-page bg-white">
    <!-- Uses TopHeaderV2 (forked from TopHeader) — same feature contract,
         main bar restyled per the Lovable v2 reference. `shouldShowLinks`
         + `scrollToSection` are preserved identically; handles about /
         impact / research / activity anchors. -->
    <top-header-v2 :user="user" :support-page-url="supportPageUrl" />

    <V2Hero
      :campaign="localCampaign"
      :hero="content.hero"
      :support-page-url="supportPageUrl"
    />

    <V2About :campaign="localCampaign" :gallery="content.gallery" :support-page-url="supportPageUrl" />

    <V2FounderCta :founder-cta="content.founder_cta" />

    <V2ResearchModel :research-model="content.research_model" />

    <V2PrecisionAnalysis :precision-analysis="content.precision_analysis" />

    <V2SupportOnboarding
      :support-onboarding="content.support_onboarding"
      :campaign="localCampaign"
    />

    <V2RecentActivity
      :campaign="localCampaign"
      :activity-section="content.activity_section"
    />

    <V2Footer
      :foundation="foundationCampaign"
      :footer="content.footer"
    />

    <V2SupportModal
      v-if="localCampaign && localCampaign.id"
      :parent-campaign="localCampaign"
      :open="supportModalOpen"
      :hero-steps-override="supportModalStepsOverride"
      @close="closeSupportModal"
      @campaign-created="onSupportCampaignCreated"
    />

    <!-- Support Hub — the "Support our campaign" takeover (fundraise +
         share). Opens from every Support entry point when the server
         supplies `supportPageUrl` (dev rollout), or on arrival via
         `?support=1` (what /campaigns/{id}/support redirects to). Its
         fundraise CTA hands off to V2SupportModal above. -->
    <V2SupportHubModal
      v-if="localCampaign && localCampaign.id && supportPageUrl"
      :open="supportHubOpen"
      :campaign="localCampaign"
      :support-onboarding="content.support_onboarding"
      :support-page-url="supportPageUrl"
      :return-focus="supportHubReturnFocus"
      @close="closeSupportHub"
      @start-fundraiser="onHubStartFundraiser"
    />

    <!-- Patient research-program enrollment modal — triggered by
         V2PrecisionAnalysis's "Request Enrollment" button via the
         `open-precision-enrollment-modal` emitter event. Receives the
         admin-edited modal copy (badge, headline, body, pillars) from
         precision_analysis.enrollment_cta so per-campaign overrides
         flow through without prop drilling. -->
    <V2PrecisionEnrollmentModal
      v-if="localCampaign && localCampaign.id"
      :open="precisionEnrollmentModalOpen"
      :modal-content="precisionEnrollmentContent"
      :campaign-id="localCampaign.id"
      @close="closePrecisionEnrollmentModal"
    />

    <!-- Mobile sticky action bar — two pill buttons (Support + Donate)
         on a frosted-glass panel, per Rick's mockup. Hidden while any
         modal is open so the z-30 bar doesn't float above the modal's
         z-20 backdrop on scroll. -->
    <div
      v-show="!isMobileMenuOpen && !donationModalOpen && !supportModalOpen && !supportHubOpen && !precisionEnrollmentModalOpen"
      class="campaign-v2-sticky-bar fixed inset-x-0 bottom-0 z-30 md:hidden"
    >
      <div class="campaign-v2-sticky-bar__inner">
        <component
          :is="supportPageUrl ? 'a' : 'button'"
          :href="supportPageUrl || undefined"
          :type="supportPageUrl ? undefined : 'button'"
          class="campaign-v2-sticky-bar__btn campaign-v2-sticky-bar__btn--support"
          @click="openSupportModalFromSticky"
        >
          <!-- Lucide heart -->
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" class="campaign-v2-sticky-bar__icon" aria-hidden="true">
            <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
          </svg>
          Support
        </component>
        <button
          type="button"
          class="campaign-v2-sticky-bar__btn campaign-v2-sticky-bar__btn--donate"
          @click="openDonationFromSticky"
        >
          <!-- Lucide gift -->
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" class="campaign-v2-sticky-bar__icon" aria-hidden="true">
            <rect x="3" y="8" width="18" height="4" rx="1"/>
            <path d="M12 8v13"/>
            <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/>
            <path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"/>
          </svg>
          Donate now
        </button>
      </div>
    </div>

    <!-- Donation modal widget — listens on the `open-donation-modal` emitter event.
         Passes the foundation's template content so the per-step bold copy is
         editable from /admin/campaign/{id}/template (Donate Copy section). -->
    <make-donation-widget
      :campaign-id="String(localCampaign.id)"
      :pledge-id="pledgeId"
      :campaign="localCampaign"
      :impact-copy-override="impactCopyOverride"
    />

    <!-- Share modal — listens on the `open-share-dialog` emitter event,
         fired by V2Hero's Share pill when navigator.share is unavailable
         (desktop browsers without the Web Share API). -->
    <v2-share-modal />
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import { clearModalIntentParam, isUnmodifiedPrimaryClick } from '../../../composables/useCampaignShare'
import { scrollToInPageAnchor } from '../../../composables/useInPageScroll'
import V2Hero from '../campaign-page/v2/V2Hero.vue'
import V2About from '../campaign-page/v2/V2About.vue'
import V2FounderCta from '../campaign-page/v2/V2FounderCta.vue'
import V2ResearchModel from '../campaign-page/v2/V2ResearchModel.vue'
import V2PrecisionAnalysis from '../campaign-page/v2/V2PrecisionAnalysis.vue'
import V2SupportOnboarding from '../campaign-page/v2/V2SupportOnboarding.vue'
import V2SupportModal from '../campaign-page/v2/V2SupportModal.vue'
import V2SupportHubModal from '../campaign-page/v2/V2SupportHubModal.vue'
import V2PrecisionEnrollmentModal from '../campaign-page/v2/V2PrecisionEnrollmentModal.vue'
import V2RecentActivity from '../campaign-page/v2/V2RecentActivity.vue'
import V2Footer from '../campaign-page/v2/V2Footer.vue'
import V2ShareModal from '../campaign-page/v2/V2ShareModal.vue'

export default {
  name: 'DigPinkCampaignPageV2',
  components: {
    V2Hero,
    V2About,
    V2FounderCta,
    V2ResearchModel,
    V2PrecisionAnalysis,
    V2SupportOnboarding,
    V2SupportModal,
    V2SupportHubModal,
    V2PrecisionEnrollmentModal,
    V2RecentActivity,
    V2Footer,
    V2ShareModal,
  },
  props: {
    campaignId: { type: [String, Number], required: true },
    pledgeId: { type: [String, Number, null], default: null },
    myCampaign: { type: Object, required: true },
    foundation: { type: Object, default: null },
    foundationTemplateContent: { type: Object, default: () => ({}) },
    user: { type: Object, default: null },
    supportPageUrl: { type: String, default: null },
    // When true (admin template-editor preview iframe), skip server-side
    // view tracking and re-normalize `foundationTemplateContent` reactively
    // so postMessage updates propagate.
    preview: { type: Boolean, default: false },
  },
  data() {
    return {
      localCampaign: { ...this.myCampaign },
      content: this.normalizeContent(this.foundationTemplateContent),
      viewTimer: null,
      // Tracks donation modal open/close state via emitter. When true,
      // the sticky bottom donate bar is hidden so it doesn't overlap
      // the open modal.
      donationModalOpen: false,
      supportModalOpen: false,
      supportHubOpen: false,
      supportHubReturnFocus: null,
      precisionEnrollmentModalOpen: false,
      isMobileMenuOpen: false,
      precisionEnrollmentContent: {},
    }
  },
  computed: {
    foundationCampaign() {
      return this.foundation || this.myCampaign?.parent || this.myCampaign
    },
    /**
     * Per-step bold copy for the donation widget, sourced from the
     * foundation's template content (Donate Copy section in
     * /admin/campaign/{id}/template). Returns the raw 5-slot array so
     * the widget can decide per-slot whether to use the override or
     * fall back to its hard-coded defaults — that way an admin can
     * customize one line without having to fill all five.
     */
    impactCopyOverride() {
      const stepCopy = this.content?.donation_widget?.step_copy
      return Array.isArray(stepCopy) ? stepCopy : null
    },
    /**
     * Per-step image + caption override for the V2 supporting-campaign
     * modal hero. 3-slot array indexed by step (0=Email, 1=Details,
     * 2=Outcome). Each slot is { image, caption } — empty fields fall
     * through to V2SupportModalHero's hard-coded defaults.
     */
    supportModalStepsOverride() {
      const steps = this.content?.support_modal?.steps
      return Array.isArray(steps) ? steps : null
    },
  },
  watch: {
    foundationTemplateContent: {
      deep: true,
      handler(next) {
        // In preview mode the content prop is driven live by postMessage.
        // Normal visitors never change this prop post-mount so this watcher
        // is a no-op on the public page.
        this.content = this.normalizeContent(next)
      },
    },
  },
  created() {
    this.emitter.on('campaign-donation-update', this.onDonationUpdate)
    // TopHeader (and legacy MyDonateButton) emit this as a bridge to the
    // donation modal. The modal listens on `open-donation-modal`, so forward.
    this.emitter.on('open-donation-modal-from-campaign', this.forwardDonateOpen)
    this.emitter.on('campaign-mobile-menu-state', this.onMobileMenuState)
    // Track donation modal state so we can hide the mobile sticky donate
    // bar while it's open (else the bar's z-30 floats above the modal
    // backdrop's z-20 and overlaps the open widget on scroll).
    this._onDonationOpen  = () => { this.donationModalOpen = true  }
    this._onDonationClose = () => { this.donationModalOpen = false }
    this.emitter.on('open-donation-modal', this._onDonationOpen)
    this.emitter.on('donation-modal-close', this._onDonationClose)
    if (!this.preview) {
      this.captureView()
      this.viewTimer = setInterval(this.captureView, 30000)
    }
  },
  mounted() {
    this.$store.commit('campaignWidget/setCampaignName', this.localCampaign.name)
    this.$store.commit(
      'campaignWidget/setCampaignParentName',
      this.localCampaign.parent ? this.localCampaign.parent.name : null
    )
    this.$store.commit('campaignWidget/setCampaign', this.localCampaign)
    this.$store.commit('campaignWidget/setPledgeId', this.pledgeId)

    this.emitter.on('open-support-modal', this.openSupportModal)
    // Preview-driven close: lets the admin template editor close the
    // modal when they navigate away from the Support Modal section.
    this.emitter.on('close-support-modal', this.closeSupportModal)
    // Patient research-program enrollment modal — V2PrecisionAnalysis
    // emits this with the section's enrollment_cta payload so per-
    // campaign hero copy reaches the modal without prop drilling.
    this.emitter.on('open-precision-enrollment-modal', this.openPrecisionEnrollmentModal)
    // Support Hub — header nav, hero, About, and the mobile sticky bar all
    // emit this instead of navigating to the old standalone support page.
    this.emitter.on('open-support-hub', this.openSupportHub)
    this.emitter.on('close-support-hub', this.closeSupportHub)

    // Open only after child components mount, so the existing modal watcher
    // initializes authentication, focus, and the shared page scroll lock.
    // `?register=1` wins over `?support=1` — never two modals at once.
    if (!this.preview) {
      const params = new URLSearchParams(window.location.search)
      if (params.get('register') === '1') {
        this.openSupportModal()
      } else if (params.get('support') === '1' && this.supportPageUrl) {
        this.openSupportHub()
      }
    }
  },
  beforeUnmount() {
    clearInterval(this.viewTimer)
    this.emitter.off('campaign-donation-update', this.onDonationUpdate)
    this.emitter.off('open-donation-modal-from-campaign', this.forwardDonateOpen)
    this.emitter.off('campaign-mobile-menu-state', this.onMobileMenuState)
    this.emitter.off('open-donation-modal', this._onDonationOpen)
    this.emitter.off('donation-modal-close', this._onDonationClose)
    this.emitter.off('open-support-modal', this.openSupportModal)
    this.emitter.off('close-support-modal', this.closeSupportModal)
    this.emitter.off('open-precision-enrollment-modal', this.openPrecisionEnrollmentModal)
    this.emitter.off('open-support-hub', this.openSupportHub)
    this.emitter.off('close-support-hub', this.closeSupportHub)
  },
  methods: {
    normalizeContent(input) {
      const source = input || {}
      return {
        hero: source.hero || {},
        founder_cta: source.founder_cta || {},
        research_model: source.research_model || { steps: [] },
        precision_analysis: source.precision_analysis || {
          what_you_get: [],
          how_it_works: [],
          who_qualifies: [],
          enrollment_cta: {},
        },
        support_onboarding: source.support_onboarding || { steps: [] },
        activity_section: source.activity_section || {},
        donation_widget: source.donation_widget || { step_copy: [] },
        support_modal: source.support_modal || { steps: [] },
        gallery: Array.isArray(source.gallery) ? source.gallery : [],
        footer: source.footer || {},
      }
    },
    onDonationUpdate(updated) {
      if (updated && updated.id && String(updated.id) === String(this.localCampaign.id)) {
        this.localCampaign = { ...this.localCampaign, ...updated }
      }
    },
    forwardDonateOpen(payload) {
      this.emitter.emit('open-donation-modal', payload)
    },
    onMobileMenuState(isOpen) {
      this.isMobileMenuOpen = Boolean(isOpen)
    },
    captureView() {
      axios.post('/capture-view', { id: this.localCampaign.id }).catch(() => {
        clearInterval(this.viewTimer)
      })
    },
    openSupportModal() {
      this.supportModalOpen = true
      window.__supportModalOpened = true
    },
    openSupportModalFromSticky(event) {
      if (this.supportPageUrl) {
        if (!isUnmodifiedPrimaryClick(event)) return
        // The bar renders an <a href="/campaigns/{id}/support"> so the link
        // is copyable; with JS the hub opens in place instead of navigating.
        event?.preventDefault?.()
        this.openSupportHub({ returnFocus: event?.currentTarget })
        return
      }
      // Jump to the in-page "Support This Campaign" section
      // (V2SupportOnboarding) instead of opening V2SupportModal — lets
      // the visitor see the fundraise + share cards side-by-side and
      // pick the option that fits them. The shared helper owns the
      // header-height offset so this anchor lands at the same y-position
      // as every other in-page anchor.
      if (!scrollToInPageAnchor('support-campaign')) {
        // Defensive fallback for any older template variant that doesn't
        // mount V2SupportOnboarding — the entry point still works.
        this.emitter.emit('open-support-modal')
      }
    },
    openDonationFromSticky() {
      this.emitter.emit('open-donation-modal')
    },
    closeSupportModal() {
      this.supportModalOpen = false
      window.__supportModalOpened = false
      if (!this.preview) clearModalIntentParam('register')
    },
    openSupportHub(payload = {}) {
      if (!this.supportPageUrl) {
        // No server-side rollout: fall back to the in-page Support section.
        this.openSupportModalFromSticky()
        return
      }
      this.supportHubReturnFocus = payload?.returnFocus || null
      this.supportHubOpen = true
    },
    closeSupportHub() {
      this.supportHubOpen = false
      if (!this.preview) clearModalIntentParam('support')
    },
    onHubStartFundraiser() {
      // Hand off from the hub to the signup wizard: close first so the hub
      // releases its focus trap + scroll lock, then open the wizard on the
      // next tick so its own `open` watcher initializes cleanly.
      this.closeSupportHub()
      this.$nextTick(() => this.openSupportModal())
    },
    onSupportCampaignCreated() {
      // Don't close the modal here — V2SupportModal advances to Step 4
      // and renders the "Your campaign is live!" outcome card with a
      // link to the new campaign. The user closes the modal themselves
      // (via the link, the close button, or ESC). This hook is reserved
      // for future side-effects like refetching parent-page stats.
    },
    openPrecisionEnrollmentModal(payload) {
      // `payload.content` is precision_analysis.enrollment_cta — the
      // admin-edited modal copy. Default to an empty object so the
      // modal falls through to its hard-coded Lovable-mockup defaults
      // when no admin copy exists yet.
      this.precisionEnrollmentContent = (payload && payload.content) || {}
      this.precisionEnrollmentModalOpen = true
    },
    closePrecisionEnrollmentModal() {
      this.precisionEnrollmentModalOpen = false
    },
  },
}
</script>

<style scoped>
/* Leave room below the footer for the always-visible mobile actions,
   including the home indicator. Match the bar's md:hidden breakpoint. */
@media (max-width: 767px) {
  .campaign-v2-page {
    padding-bottom: calc(6rem + env(safe-area-inset-bottom, 0px));
  }
}

/* Frosted-glass panel. Translucent white over the page content, blurred
   so type underneath softens (iOS-26-style). Same recipe as
   V2HeroEvents' frosted cards (rgba white + blur + saturate + hairline
   border). Padding accounts for the iOS home indicator via
   safe-area-inset-bottom. */
.campaign-v2-sticky-bar__inner {
  display: flex;
  gap: 12px;
  padding: 14px 16px calc(14px + env(safe-area-inset-bottom, 0px));
  background: rgba(255, 255, 255, 0.72);
  border-top: 1px solid rgba(255, 255, 255, 0.55);
  box-shadow: 0 -8px 24px -12px rgba(29, 37, 73, 0.18);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
}

/* Pill buttons — split 50/50, no gradients, no shadows (per V2 button
   convention). Match V2About primary-button sizing for cross-section
   consistency. */
.campaign-v2-sticky-bar__btn {
  flex: 1 1 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 0;
  border-radius: 9999px;
  padding: 13px 18px;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.01em;
  color: #ffffff;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 150ms ease, transform 150ms ease, opacity 150ms ease;
}
.campaign-v2-sticky-bar__btn:active { transform: scale(0.98); }
.campaign-v2-sticky-bar__btn:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.85);
  outline-offset: 2px;
}

/* Brand blue — sass/_variables.scss legacy palette ($blue: #009cde,
   darker: #0075a7). No CSS custom property exists for blue, so hex
   is intentional (mirrors the donation-widget tribute toggle). */
.campaign-v2-sticky-bar__btn--support {
  background-color: #009CDE;
}
.campaign-v2-sticky-bar__btn--support:hover {
  background-color: #0075A7;
}

.campaign-v2-sticky-bar__btn--donate {
  background-color: var(--sideout-pink);
}
.campaign-v2-sticky-bar__btn--donate:hover {
  background-color: var(--sideout-pink-hover);
}

.campaign-v2-sticky-bar__icon {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
}
</style>
